import Api from "@/Api";
import { AppThunk } from "@/hooks/app";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  IMessage,
  MessageCursorRequest,
  MessageRequest,
  MessageWindowRequest,
  MessageWindowResponse,
  IRoom,
  RoomUpdateRequest,
} from "@/interfaces";
import { fetchUserInfoThunk, shiftRoom, updateLocalUserRoom } from "./user";
import { IReadMessage } from "@/core";
import { mergeReadSeqForward } from "./readSeq";
import {
  patchMessageUserReferences,
  patchRoomUserReferences,
  updateUserReferences,
} from "./userReferences";

export type MessageLoadingKind = "initial" | "before" | "after" | "window";

export interface IState {
  id: string;
  scrollToEnd?: number;
  scrollToTop?: number;
  error: string | null;
  data: IRoom;
  loadingMessage: boolean;
  loadingMessageKind: MessageLoadingKind | null;
  selectedMessage?: IMessage;
  replyMessage?: IMessage;
}

const initialState: IState = {
  id: "", // room id
  scrollToEnd: undefined,
  scrollToTop: undefined,
  loadingMessage: false,
  loadingMessageKind: null,
  error: null,
  data: {
    id: "",
    message: [],
    hasMoreMessage: true,
    totalCount: 0,
    image: "",
    name: "",
    member: [],
    memberId: [],
    channelType: "PRIVATE",
    // lastMsg?: {},
    creatorId: "",
    adminId: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    readSeq: {},
    admin: [],
  },
  selectedMessage: undefined,
};

// 加载房间基本信息
export const getRoomInfoThunk = createAsyncThunk<void, string>(
  `fetchRoomInfo`,
  async (id, { dispatch }) => {
    // 修改当前面room id
    dispatch(changeRoomId(id));
    // 加载中
    dispatch(changeLoading({ kind: "initial", loading: true }));
    try {
      // 获取当前房间基本信息
      const res = await Api.getRoom({ id });
      // 将当前房间基本信息存到store里面
      dispatch(initialMessage(res));
      // Public rooms can be previewed by outsiders, but message history is
      // member-only. Joining will run this thunk again and load the history.
      if (res.isMember === false) {
        dispatch(
          initialMessage({
            message: [],
            totalCount: 0,
            hasMoreMessage: false,
            hasMoreBefore: false,
            hasMoreAfter: false,
            messageWindowMode: false,
          })
        );
        return;
      }
      const messagePage = await Api.getRoomMessages({
        pageSize: 50,
        id,
      });
      dispatch(
        initialMessage({
          message: messagePage.message,
          hasMoreMessage: messagePage.hasMore,
          hasMoreBefore: messagePage.hasMore,
          hasMoreAfter: false,
          messageWindowMode: false,
        })
      );
      // div元素撑开后，滚动到底部
      dispatch(scrollToEnd(true));
    } finally {
      // Loading must also end when room/history requests are rejected.
      dispatch(changeLoading({ kind: "initial", loading: false }));
    }
  }
);
// 加载更多消息
export const loadRoomMoreMessageThunk = createAsyncThunk<
  { message: IMessage[]; hasMore: boolean },
  MessageRequest
>(`loadRoomMoreMessageThunk`, async (data, { dispatch }) => {
  dispatch(changeLoading({ kind: "before", loading: true }));
  try {
    return await Api.getRoomMessages(data);
  } finally {
    dispatch(changeLoading({ kind: "before", loading: false }));
  }
});

export const addRoomThunk = createAsyncThunk<IRoom, Partial<IRoom>>(
  `addRoom`,
  async (data, { dispatch }) => {
    const res = await Api.addRoom(data);
    dispatch(shiftRoom(res));
    return res;
  }
);

export const loadRoomCursorMessagesThunk = createAsyncThunk<
  { message: IMessage[]; hasMore: boolean },
  MessageCursorRequest
>(`loadRoomCursorMessagesThunk`, async (data, { dispatch }) => {
  const kind = data.direction === "before" ? "before" : "after";
  dispatch(changeLoading({ kind, loading: true }));
  try {
    return await Api.getRoomMessagesByCursor(data);
  } finally {
    dispatch(changeLoading({ kind, loading: false }));
  }
});

export const openMessageWindowThunk = createAsyncThunk<
  MessageWindowResponse,
  MessageWindowRequest
>(`openMessageWindowThunk`, async (data, { dispatch }) => {
  dispatch(changeLoading({ kind: "window", loading: true }));
  try {
    const windowData = await Api.getRoomMessageWindow(data);
    dispatch(openMessageWindow(windowData));
    return windowData;
  } finally {
    dispatch(changeLoading({ kind: "window", loading: false }));
  }
});

const preserveActiveMessageState = (
  room: IRoom,
  currentRoom: IRoom
): IRoom => ({
  ...room,
  message: currentRoom.message,
  totalCount: currentRoom.totalCount,
  hasMoreMessage: currentRoom.hasMoreMessage,
  hasMoreBefore: currentRoom.hasMoreBefore,
  hasMoreAfter: currentRoom.hasMoreAfter,
  messageWindowMode: currentRoom.messageWindowMode,
});

export const refreshRoomInfoThunk =
  (id: string): AppThunk<Promise<IRoom>> =>
  async (dispatch, getState) => {
    const room = await Api.getRoom({ id });
    const currentRoom = getState().room.data as IRoom;
    if (String(currentRoom.id) === String(id)) {
      dispatch(initialMessage(preserveActiveMessageState(room, currentRoom)));
    }
    dispatch(updateLocalUserRoom(room));
    return room;
  };

export const updateRoomThunk =
  (data: RoomUpdateRequest): AppThunk<Promise<IRoom>> =>
  async (dispatch, getState) => {
    const room = await Api.updateRoom(data);
    const currentRoom = getState().room.data as IRoom;
    dispatch(initialMessage(preserveActiveMessageState(room, currentRoom)));
    dispatch(updateLocalUserRoom(room));
    return room;
  };

export const joinRoomThunk = createAsyncThunk<IRoom, { id?: string }>(
  "joinRoom",
  async (data, { dispatch }) => {
    const res = await Api.joinRoom(data);
    await dispatch(fetchUserInfoThunk());
    await dispatch(getRoomInfoThunk(String(res.id)));
    return res;
  }
);

export const roomSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    changeLoading(
      state,
      action: PayloadAction<{
        kind: MessageLoadingKind;
        loading: boolean;
      }>
    ) {
      const { kind, loading } = action.payload;
      if (loading) {
        state.loadingMessage = true;
        state.loadingMessageKind = kind;
        return;
      }
      if (state.loadingMessageKind === kind) {
        state.loadingMessage = false;
        state.loadingMessageKind = null;
      }
    },
    scrollToTop(state, action) {
      state.scrollToTop = action.payload ? Math.random() : -Math.random();
    },
    scrollToEnd(state, action) {
      state.scrollToEnd = action.payload ? Math.random() : -Math.random();
    },
    loadMoreMessage(state, action: PayloadAction<IMessage[]>) {
      state.data.message = [...action.payload, ...state.data.message];
    },
    appendMoreMessage(state, action: PayloadAction<IMessage[]>) {
      const existing = new Set(
        state.data.message.map((item) => String(item.id))
      );
      state.data.message.push(
        ...action.payload.filter((item) => !existing.has(String(item.id)))
      );
    },
    setHasMoreMessage(state, action: PayloadAction<boolean>) {
      state.data.hasMoreMessage = action.payload;
      state.data.hasMoreBefore = action.payload;
    },
    setHasMoreAfter(state, action: PayloadAction<boolean>) {
      state.data.hasMoreAfter = action.payload;
    },
    mergeMessages(state, action: PayloadAction<IMessage[]>) {
      const merged = [...state.data.message, ...action.payload];
      const unique = new Map<string, IMessage>();
      for (const message of merged) {
        unique.set(String(message.id), message);
      }
      state.data.message = Array.from(unique.values()).sort(
        (a, b) => a.seq - b.seq
      );
    },
    changeRoomId(state, action) {
      state.id = action.payload;
    },
    addMessage(state, action: PayloadAction<IMessage>) {
      if (state.data.messageWindowMode && state.data.hasMoreAfter) {
        return state;
      }
      state.data.message.push(action.payload);
      return state;
    },
    replaceMessage(
      state,
      action: PayloadAction<{ id: string; message: IMessage }>
    ) {
      const index = state.data.message.findIndex(
        (message) => String(message.id) === String(action.payload.id)
      );
      if (index >= 0) {
        state.data.message[index] = action.payload.message;
        return state;
      }
      state.data.message.push(action.payload.message);
      return state;
    },
    updateMessageStatus(
      state,
      action: PayloadAction<{
        id: string;
        localStatus: IMessage["localStatus"];
      }>
    ) {
      const message = state.data.message.find(
        (item) => String(item.id) === String(action.payload.id)
      );
      if (message) {
        message.localStatus = action.payload.localStatus;
      }
    },
    appendRoomUsers(
      state,
      action: PayloadAction<{
        role: "member" | "admin";
        users: IRoom["member"];
        totalCount?: number;
      }>
    ) {
      const currentUsers =
        action.payload.role === "admin"
          ? (state.data.admin ?? [])
          : (state.data.member ?? []);
      const unique = new Map<string, IRoom["member"][number]>();
      for (const user of currentUsers) {
        unique.set(String(user.id), user);
      }
      for (const user of action.payload.users ?? []) {
        unique.set(String(user.id), user);
      }
      const users = Array.from(unique.values());
      if (action.payload.role === "admin") {
        state.data.admin = users;
        if (typeof action.payload.totalCount === "number") {
          state.data.adminTotalCount = action.payload.totalCount;
        }
      } else {
        state.data.member = users;
        if (typeof action.payload.totalCount === "number") {
          state.data.memberTotalCount = action.payload.totalCount;
        }
      }
    },
    initialMessage(state, action: PayloadAction<Partial<IRoom>>) {
      state.data = { ...state.data, ...action.payload };
    },
    openMessageWindow(state, action: PayloadAction<MessageWindowResponse>) {
      state.data.message = action.payload.message;
      state.data.totalCount = action.payload.totalCount;
      state.data.hasMoreMessage = action.payload.hasMoreBefore;
      state.data.hasMoreBefore = action.payload.hasMoreBefore;
      state.data.hasMoreAfter = action.payload.hasMoreAfter;
      state.data.messageWindowMode = true;
    },
    // sync method, just modify state
    markReadMessage(
      state,
      action: PayloadAction<{ id: string; readSeq?: IReadMessage["readSeq"] }>
    ) {
      const room = action.payload;
      if (room.id === state.id) {
        state.data.readSeq ??= {};
        mergeReadSeqForward(state.data.readSeq, room.readSeq ?? {});
      }
    },
    updateSelectedMessage(state, action: PayloadAction<IMessage | undefined>) {
      const message = action.payload;
      Object.assign(state, {
        selectedMessage: message,
      });
    },
    recallExistMessage(state, action: PayloadAction<IMessage>) {
      const message = action.payload;
      if (state.data.id !== message.channelId) return;
      const msg = state.data.message.find((msg) => msg.id === message.id);
      if (msg) {
        Object.assign(msg, message);
      }
    },
    updateReplyMessage(state, action: PayloadAction<IMessage | undefined>) {
      const message = action.payload;
      Object.assign(state, {
        replyMessage: message,
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateUserReferences, (state, action) => {
      patchRoomUserReferences(state.data, action.payload);
      patchMessageUserReferences(state.selectedMessage, action.payload);
      patchMessageUserReferences(state.replyMessage, action.payload);
    });
  },
});

export const {
  scrollToTop,
  scrollToEnd,
  addMessage,
  replaceMessage,
  loadMoreMessage,
  appendMoreMessage,
  setHasMoreMessage,
  setHasMoreAfter,
  mergeMessages,
  appendRoomUsers,
  initialMessage,
  openMessageWindow,
  changeLoading,
  changeRoomId,
  markReadMessage,
  updateMessageStatus,
  updateSelectedMessage,
  recallExistMessage,
  updateReplyMessage,
} = roomSlice.actions;

export const roomReducer = roomSlice.reducer;
